import { getTokenInfo, useAuth0 } from "../../contexts/AuthManager/Auth";

import { CustomerListItem } from "components/Dashboard/Tiles/CustomerTiles/customerTilesUtils";
import { atomWithStorage } from "jotai/utils";
import { useAtom } from "jotai";
import { SetStateAction, useMemo, useState } from "react";
import { useEffectOnce, useInterval } from "utils/customHooks/genericHooks";
import { decodeToken } from "contexts/AuthManager/jwtHelper";
import { fetchCustomerJson } from "../../components/DataGetter/CustomerJsonGetter";
import { uniq } from "lodash";
import { INVALID_CUSTOMERS, MAGIC_STRING } from "constants/constants";

const customersListAtom = atomWithStorage<CustomerListItem[]>(
  "customersListAtom",
  []
);
export function useCustomersList(): [
  customersList: CustomerListItem[],
  setCustomersList: (update: SetStateAction<CustomerListItem[]>) => void,
] {
  return useAtom(customersListAtom);
}

export function CustomersListGetter() {
  // grab the thing on the first go
  const [customersList, setCustomersList] = useAtom(customersListAtom);
  const customersListCkeys = useMemo(
    () => customersList.map(c => c.abbrevName),
    [customersList]
  );

  const { isLoading: authLoading, isAuthenticated } = useAuth0();

  const { idToken } = getTokenInfo();
  const decoded = idToken && decodeToken(idToken);
  const [newCustomersQueue, setNewCustomersQueue] = useState<
    CustomerListItem[]
  >([]);
  // fetch all customers on mount
  useEffectOnce({
    callback: () => {
      const customers =
        decoded?.["https://raven.ai/app_metadata"]?.customer ??
        decoded?.app_metadata?.customer ??
        decoded?.customer ??
        [];
      uniq<string>(customers)
        .filter((ckey: string) => !customersListCkeys.includes(ckey))
        .forEach(ckey => {
          if ([MAGIC_STRING, ...INVALID_CUSTOMERS].includes(ckey)) {
            return;
          }
          fetchCustomerJson(ckey).then(data => {
            if (data) {
              const newCustomerItem = {
                abbrevName: data.abbrevName.toLowerCase(),
                customerName: data.customerName,
              };
              if (
                !data.abbrevName ||
                // skip if already in local storage or queued
                customersListCkeys.includes(data.abbrevName) ||
                newCustomersQueue.some(
                  queuedItem => queuedItem.abbrevName === data.abbrevName
                )
              ) {
                return;
              }
              setNewCustomersQueue(prev => [...prev, newCustomerItem]);
            }
          });
        });
    },
    dependencies: [isAuthenticated, authLoading],
    shouldRun: isAuthenticated && !authLoading,
  });

  useInterval({
    callback: () => {
      if (newCustomersQueue.length === 0) {
        return;
      }
      setCustomersList(prev => [...prev, ...newCustomersQueue]);
      setNewCustomersQueue([]);
    },
    delay: newCustomersQueue.length === 0 ? 0 : 1 * 1000,
    immediate: false,
  });

  return null;
}
